/**
 * Safely gets a nested value from an object using a dot-separated path string.
 * Returns undefined if the path doesn't exist.
 *
 * @param obj - The object to get the value from
 * @param path - Dot-separated path string (e.g., "user.profile.name")
 * @returns The value at the path, or undefined if not found
 *
 * @example
 * const obj = { user: { profile: { name: 'John' } } };
 * getNestedValue(obj, 'user.profile.name'); // 'John'
 * getNestedValue(obj, 'user.profile.age'); // undefined
 */
export function getNestedValue<T = unknown>(
  obj: unknown,
  path: string
): T | undefined {
  if (!obj || typeof obj !== 'object' || !path) {
    return undefined;
  }

  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return undefined;
    }

    current = (current as Record<string, unknown>)[key];

    if (current === undefined) {
      return undefined;
    }
  }

  return current as T;
}
